import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, ImageIcon, MessageSquare, BarChart3, LogOut, ChevronRight, Newspaper, Instagram, FileStack, FolderOpen, Megaphone, TrendingUp, LayoutDashboard, ScanLine, BriefcaseBusiness, Database, ListTodo, Activity, Users, Wand2, TerminalSquare, MessagesSquare, Sparkles, PlugZap, Bell, Send } from 'lucide-react';
import AdminTeamChat from './AdminTeamChat';
import AdminMobileNav from '@/components/admin/AdminMobileNav';
import InstallAppButton from '@/components/admin/team/InstallAppButton';
import AdminCallDock from '@/components/admin/team/AdminCallDock';
import { base44 } from '@/api/base44Client';
import { setSEO } from '@/lib/seo';
import AdminDashboard from './AdminDashboard';
import AdminProducts from './AdminProducts';
import AdminReferences from './AdminReferences';
import AdminPoptavky from './AdminPoptavky';
import AdminAnalytics from './AdminAnalytics';
import AdminBlog from './AdminBlog';
import AdminInstagram from './AdminInstagram';
import AdminPages from './AdminPages';
import AdminMedia from './AdminMedia';
import AdminMarketing from './AdminMarketing';
import AdminProductAnalytics from './AdminProductAnalytics';
import AdminReferenceAnalytics from './AdminReferenceAnalytics';
import AdminAR from './AdminAR';
import AdminDatabricks from './AdminDatabricks';
import AdminTasks from './AdminTasks';
import AdminSystemDevelopment from './AdminSystemDevelopment';
import AdminProspects from './AdminProspects';
import AdminCRM from './AdminCRM';
import AdminTerminal from './AdminTerminal';
import AdminIntegrations from './AdminIntegrations';
import { Bot } from 'lucide-react';
import ProduktovyOptimalizatorChat from '@/components/admin/ProduktovyOptimalizatorChat';
import BrandIconStudio from '@/components/admin/brand/BrandIconStudio';
import AdminMcpConnect from './AdminMcpConnect';
import AdminNotifications from './AdminNotifications';
import AdminOffers from './AdminOffers';

const TABS = [
  { id: 'dashboard', label: 'Přehled', icon: LayoutDashboard },
  { id: 'terminal', label: 'Terminál', icon: TerminalSquare },
  { id: 'chat', label: 'Týmový chat', icon: MessagesSquare },
  { id: 'development', label: 'Vývoj systému', icon: Activity },
  { id: 'integrations', label: 'Integrace', icon: PlugZap },
  { id: 'tasks', label: 'Úkoly & tým', icon: ListTodo },
  { id: 'products', label: 'Produkty', icon: Package },
  { id: 'ai-optimalizace', label: 'AI optimalizace', icon: Wand2 },
  { id: 'brand-visuals', label: 'AI ikony & vizuály', icon: Sparkles },
  { id: 'product-analytics', label: 'Produktová analýza', icon: TrendingUp },
  { id: 'reference-analytics', label: 'Analýza realizací', icon: BarChart3 },
  { id: 'references', label: 'Reference', icon: ImageIcon },
  { id: 'blog', label: 'Blog', icon: Newspaper },
  { id: 'pages', label: 'Stránky', icon: FileStack },
  { id: 'media', label: 'Media', icon: FolderOpen },
  { id: 'marketing', label: 'Marketing', icon: Megaphone },
  { id: 'prospects', label: 'Prospecting', icon: Users },
  { id: 'crm', label: 'CRM Corporate', icon: BriefcaseBusiness },
  { id: 'poptavky', label: 'Poptávky', icon: MessageSquare },
  { id: 'offers', label: 'Odeslané nabídky', icon: Send },
  { id: 'ar', label: 'AR návrhy', icon: ScanLine },
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'databricks', label: 'Databricks', icon: Database },
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'chatgpt', label: 'ChatGPT / MCP', icon: Bot },
  { id: 'notifications', label: 'Notifikace', icon: Bell },
];

const TAB_GROUPS = [
  { label: 'Řízení', ids: ['dashboard', 'tasks', 'chat'] },
  { label: 'Obchod', ids: ['poptavky', 'offers', 'crm', 'prospects'] },
  { label: 'Obsah & produkty', ids: ['products', 'ai-optimalizace', 'brand-visuals', 'references', 'blog', 'pages', 'media', 'marketing', 'instagram', 'ar'] },
  { label: 'Data & systém', ids: ['product-analytics', 'reference-analytics', 'analytics', 'integrations', 'chatgpt', 'development', 'databricks', 'terminal', 'notifications'] },
];

export default function Admin() {
  const navigate = useNavigate();

  useEffect(() => {
    setSEO({ title: 'Administrace MLŽIDLA®', robots: 'noindex, nofollow, noarchive, nosnippet' });
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(() => TABS.some((tab) => tab.id === requestedTab) ? requestedTab : 'dashboard');

  useEffect(() => {
    if (requestedTab && TABS.some((tab) => tab.id === requestedTab) && requestedTab !== activeTab) setActiveTab(requestedTab);
  }, [requestedTab]);

  const changeTab = (tabId) => {
    setActiveTab(tabId);
    setSearchParams(tabId === 'dashboard' ? {} : { tab: tabId }, { replace: true });
  };

  useEffect(() => {
    const resolveAdmin = async () => {
      try {
        const u = await base44.auth.me();
        setUser(u);
        if (!u) navigate('/admin-login', { replace: true });
      } catch (_error) {
        navigate('/admin-login', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    resolveAdmin();
  }, [navigate]);

  if (loading) return (
    <div className="min-h-screen bg-ink flex items-center justify-center">
      <div className="w-6 h-6 border border-white/20 border-t-cyan rounded-full animate-spin" />
    </div>
  );

  if (!user) return null;

  // Oprávnění určuje serverem přiřazená role Base44, ne pevný seznam e-mailů.
  if (user.role !== 'admin') return (
    <div className="min-h-screen bg-ink flex items-center justify-center text-center px-6">
      <div>
        <p className="text-4xl mb-4">🔒</p>
        <h1 className="text-white text-xl font-light mb-2">Přístup odepřen</h1>
        <p className="text-white/40 text-sm">Tato stránka je dostupná pouze pro administrátory.</p>
        <a href="/" className="mt-6 inline-block text-cyan text-sm hover:underline">← Zpět na web</a>
      </div>
    </div>
  );

  const ActiveComponent = {
    dashboard: AdminDashboard,
    terminal: AdminTerminal,
    chat: AdminTeamChat,
    development: AdminSystemDevelopment,
    integrations: AdminIntegrations,
    tasks: AdminTasks,
    products: AdminProducts,
    'ai-optimalizace': ProduktovyOptimalizatorChat,
    'brand-visuals': BrandIconStudio,
    'product-analytics': AdminProductAnalytics,
    'reference-analytics': AdminReferenceAnalytics,
    references: AdminReferences,
    blog: AdminBlog,
    pages: AdminPages,
    media: AdminMedia,
    marketing: AdminMarketing,
    prospects: AdminProspects,
    crm: AdminCRM,
    poptavky: AdminPoptavky,
    offers: AdminOffers,
    ar: AdminAR,
    analytics: AdminAnalytics,
    databricks: AdminDatabricks,
    instagram: AdminInstagram,
    chatgpt: AdminMcpConnect,
    notifications: AdminNotifications,
  }[activeTab];

  return (
    <div className="min-h-screen bg-ink flex flex-col md:flex-row">
      <AdminMobileNav tabs={TABS} activeTab={activeTab} onChange={changeTab} />
      {/* Sidebar */}
      <div className="hidden w-64 bg-[#0d1117] border-r border-white/8 md:flex flex-col shrink-0">
        <div className="px-5 py-6 border-b border-white/8">
          <p className="text-xs font-mono text-white/30 tracking-widest uppercase mb-1">Admin</p>
          <p className="text-white text-sm font-medium truncate">{user.full_name || user.email}</p>
        </div>
        <nav className="flex-1 overflow-y-auto p-3">
          {TAB_GROUPS.map((group) => (
            <div key={group.label} className="mb-4 last:mb-0">
              <p className="mb-1.5 px-3 font-mono text-[9px] uppercase tracking-[.16em] text-white/20">{group.label}</p>
              <div className="space-y-1">
                {group.ids.map((tabId) => TABS.find((item) => item.id === tabId)).filter(Boolean).map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button key={tab.id} onClick={() => changeTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all ${activeTab === tab.id ? 'bg-cyan/10 text-cyan border border-cyan/20' : 'text-white/50 hover:text-white hover:bg-white/5'}`}>
                      <Icon size={16} />
                      {tab.label}
                      {activeTab === tab.id && <ChevronRight size={12} className="ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-3 border-t border-white/8 space-y-1">
          <InstallAppButton className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/5 transition-all" />
          <button onClick={() => navigate('/obchodni-nabidky')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-cyan/80 hover:text-cyan hover:bg-cyan/10 transition-all">
            <BriefcaseBusiness size={16} /> Sales Hub
          </button>
          <button onClick={() => navigate('/admin-logout')}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/40 hover:text-white hover:bg-white/5 transition-all">
            <LogOut size={16} /> Odhlásit
          </button>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 min-w-0 overflow-auto">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          <ActiveComponent />
        </motion.div>
        <div className="p-4 md:hidden">
          <InstallAppButton className="w-full flex items-center justify-center gap-2 rounded-xl border border-cyan/20 bg-cyan/10 px-3 py-3 text-sm text-cyan" />
        </div>
      </div>
      {activeTab !== 'chat' && (
        <AdminCallDock user={user} contextLabel={TABS.find((t) => t.id === activeTab)?.label || 'Administrace'} />
      )}
    </div>
  );
}